import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import backend from "../../../../../backend/backend";
import AnalyticsFilters from "./AnalyticsFilters";
import KpiTiles from "./KpiTiles";
import RequestChart from "./RequestChart";
import RequestLogTable from "./RequestLogTable";
import RequestLogDetailPanel from "./RequestLogDetailPanel";
import Spinner from "../../../../../assets/icons/Spinner";
import "./GatewayAnalyticsTab.scss";

const GatewayAnalyticsTab = ({ gateway }) => {
  // Global filters
  const [startDate, setStartDate] = useState(() =>
    dayjs().subtract(7, "day").startOf("day").toDate()
  );
  const [endDate, setEndDate] = useState(() => dayjs().endOf("day").toDate());
  const [modelId, setModelId] = useState(null);

  // Data state
  const [stats, setStats] = useState(null);
  const [timeSeries, setTimeSeries] = useState(null);
  const [logs, setLogs] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeSeriesError, setTimeSeriesError] = useState(null);
  const [selectedLogUid, setSelectedLogUid] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const pageSize = 20;
  const isInitialLoad = useRef(true);

  const handleReload = () => {
    setReloadKey((prev) => prev + 1);
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setPage(0);
  };

  const handleModelChange = (newModelId) => {
    setModelId(newModelId);
    setPage(0);
  };

  // Load stats
  useEffect(() => {
    if (!gateway?.uid) return;

    // Only show loading spinner on initial load, not on filter changes
    if (isInitialLoad.current) {
      setLoading(true);
    }
    setError(null);

    backend
      .getGatewayStats(gateway.uid, modelId, startDate, endDate)
      .then((data) => {
        setStats(data);
        setLoading(false);
        isInitialLoad.current = false;
      })
      .catch((err) => {
        console.error("Failed to load stats:", err);
        setError("Failed to load analytics data");
        setLoading(false);
        isInitialLoad.current = false;
      });
  }, [gateway?.uid, modelId, startDate, endDate, reloadKey]);

  // Load time series
  useEffect(() => {
    if (!gateway?.uid) return;

    setTimeSeriesError(null);
    backend
      .getGatewayTimeSeries(gateway.uid, modelId, startDate, endDate)
      .then(setTimeSeries)
      .catch((err) => {
        console.error("Failed to load time series:", err);
        setTimeSeriesError(err);
        setTimeSeries(null);
      });
  }, [gateway?.uid, modelId, startDate, endDate, reloadKey]);

  // Load logs
  useEffect(() => {
    if (!gateway?.uid) return;

    backend
      .getGatewayLogs(gateway.uid, page, pageSize, modelId, startDate, endDate)
      .then(setLogs)
      .catch((err) => console.error("Failed to load logs:", err));
  }, [gateway?.uid, page, modelId, startDate, endDate, reloadKey]);

  if (loading) {
    return (
      <div className="gateway_analytics loading">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="gateway_analytics error">
        <p>{error}</p>
        <p className="hint">
          Make sure the gateway service is running and accessible.
        </p>
      </div>
    );
  }

  return (
    <div className="gateway_analytics">
      <AnalyticsFilters
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange}
        modelId={modelId}
        modelIds={stats?.modelIds || []}
        onModelChange={handleModelChange}
        onReload={handleReload}
      />
      <KpiTiles stats={stats} timeSeries={timeSeries} />
      <RequestChart data={timeSeries} interval={timeSeries?.interval} error={timeSeriesError} />
      <RequestLogTable
        data={logs}
        page={page}
        onPageChange={setPage}
        onRowClick={setSelectedLogUid}
      />
      <RequestLogDetailPanel
        logUid={selectedLogUid}
        isOpen={!!selectedLogUid}
        onClose={() => setSelectedLogUid(null)}
      />
    </div>
  );
};

export default GatewayAnalyticsTab;
